import { Check, Sparkles, Play, BarChart3, Watch, BookOpen, Video } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import appScreen1 from '@/assets/app-screen-1.png';
import appScreen2 from '@/assets/app-screen-2.png';
import appScreen3 from '@/assets/app-screen-3.png';
import proBadge from '@/assets/pro-badge.png';

const features = [
  {
    title: '12týdenní tréninkové plány',
    description: 'Pro systematický rozvoj kondice i techniky',
    badge: 'PRO',
    icon: BarChart3,
  },
  {
    title: 'Videokurz kraulu',
    description: 'Spolu s tipy na technická cvičení',
    badge: 'PRO',
    icon: Play,
  },
  {
    title: 'Analýza vašeho videa',
    description: 'Možnost poslat video k základní analýze techniky a nebo konzultace s trenérem',
    badge: 'Extra',
    icon: Video,
  },
  {
    title: 'Tréninkový deník',
    description: 'Zápis tréninků a sledování vašeho pokroku',
    badge: 'FREE',
    icon: BookOpen,
  },
  {
    title: 'Integrace s Apple/Google health',
    description: 'A následná možnost zapsat si trénink do aplikace',
    badge: 'FREE',
    icon: Watch,
  },
  {
    title: 'Plavecký blog',
    description: 'Užitečné informace a tipy od odborníků',
    badge: 'FREE',
    icon: Sparkles,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export default function Features() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const screensRef = useRef(null);
  const screensInView = useInView(screensRef, { once: true, margin: "-100px" });

  return (
    <section className="py-24 md:py-36 px-4 bg-muted relative overflow-hidden" ref={ref}>
      {/* Background decoration */}
      <div className="absolute top-1/4 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto relative">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-block text-primary font-semibold text-sm uppercase tracking-wider mb-4">
            Funkce aplikace
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground">
            Co Swimmie umí
          </h2>
          <p className="text-foreground/70 text-lg max-w-2xl mx-auto leading-relaxed">
            V aplikaci najdete vše, co potřebujete k dosažení vašich plaveckých cílů.
          </p>
        </motion.div>

        {/* Features grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              variants={cardVariants}
              className="group"
            >
              <div className="h-full p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all duration-500 hover:-translate-y-2">
                <div className="flex items-start justify-between mb-4">
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <feature.icon className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                  </div>

                  {/* Badge */}
                  {feature.badge === 'PRO' ? (
                    <img src={proBadge} alt="PRO" className="h-6" />
                  ) : feature.badge === 'Extra' ? (
                    <span className="badge-extra">{feature.badge}</span>
                  ) : (
                    <span className="badge-free">{feature.badge}</span>
                  )}
                </div>

                <h3 className="font-semibold text-lg mb-2 text-foreground group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground/70 leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover indicator */}
                <div className="mt-4 flex items-center text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  <Check className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">K dispozici</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* App screenshots */}
        <div ref={screensRef} className="relative">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={screensInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Podívejte se do aplikace
            </h3>
            <p className="text-foreground/70">
              Intuitivní design pro maximální pohodlí při trénování
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[appScreen1, appScreen2, appScreen3].map((screen, idx) => (
              <motion.div
                key={idx}
                className="relative group"
                initial={{ opacity: 0, y: 40 }}
                animate={screensInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: idx * 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-primary/20 to-primary/5 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Screenshot */}
                <img
                  src={screen}
                  alt={`Aplikace screenshot ${idx + 1}`}
                  className="relative rounded-2xl w-full object-cover shadow-2xl group-hover:shadow-primary/20 transition-shadow duration-500"
                />

                {/* Float animation on hover */}
                <div className="absolute inset-0 group-hover:-translate-y-2 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
